import type {
  LocationChoice,
  RegistryCityItem,
  ResolveLocationResult,
  UserPreferencesV2,
} from '@buzzmap/shared';
import {
  ANON_PREFS_KEY,
  DEFAULT_RADIUS_KM,
  LEGACY_PREFS_KEY,
  userPrefsStorageKey,
  type PrefsSyncNotice,
} from './useUserPrefsSync';
import { resolveRegistryPair } from '../utils/registryLookup';
import { resolvePoint } from '../utils/gpsPlacement';

/**
 * Versioned v2 preference store (T011-FE, canonical territories plan).
 *
 * SEPARATE NAMESPACE from the v1 stores: `fieravicino_user_prefs*` stays
 * readable and intact for the pre-activation (`legacy`) mode and for any old
 * tab still running the previous bundle (plan l.74). The v2 namespace is
 * written only once the discovery gate reports `v2`, and an initialized v2
 * record is NEVER re-imported over by a v1 store — a v1 value that changed
 * after conversion is reported as a recoverable conflict instead
 * (`convertedFrom` fingerprint).
 *
 * Schema (v: 1): `UserPreferencesV2` — `location` is `none` or a single
 * comune UUID, `radiusKm` is an INDEPENDENT sibling of the location union so
 * `none`/`needs_confirmation` keeps its radius without activating geography
 * (plan l.69). No city arrays, no raw coordinates, no nearest-derived name,
 * no radius embedded in the location.
 */

export const V2_ANON_KEY = 'buzzmap_prefs_v2:anon';
export const V2_USER_KEY_PREFIX = 'buzzmap_prefs_v2:user:';

/** Per-account v2 key (cache + outbox). */
export function v2UserKey(userId: string): string {
  return V2_USER_KEY_PREFIX + userId;
}

/** `null` = the anonymous view; a string = the per-account cache + outbox. */
export function v2StoreKey(owner: string | null): string {
  return owner === null ? V2_ANON_KEY : v2UserKey(owner);
}

export type V2ProvenanceField = 'anon' | 'local-only';

export interface V2Provenance {
  location: V2ProvenanceField;
  favoriteCategories: V2ProvenanceField;
}

/** Explicit v2 mutation intent (the v2 outbox entry). Absent key = untouched. */
export interface V2PrefsPatch {
  municipalityId?: string | null;
  radiusKm?: number | null;
  favoriteCategories?: string[];
}

export interface V2StoredRecord {
  v: 1;
  prefs: UserPreferencesV2;
  provenance: V2Provenance;
  /** Queued offline intents, flushed with `ifRevision` for THIS owner only. */
  pending: V2PrefsPatch | null;
  /** Classified migration/server outcome: identity is NULL, radius/favorites kept. */
  locationNeedsConfirmation?: boolean;
  /**
   * FNV-1a HASH of the v1 location at conversion time — the old-tab drift
   * detector. Deliberately NOT the JSON: the record must never re-embed the
   * raw coordinates the conversion just stripped (see `fingerprintV1Location`).
   */
  convertedFrom?: string;
  /**
   * H1 (security sweep): when the conversion was DURABLE the raw v1 GPS entry
   * was rewritten in place (`rewriteV1GpsLocations`), so `convertedFrom` no
   * longer matches what the v1 store now holds. This is the fingerprint of the
   * entry the conversion LEFT BEHIND — the drift detector that stays valid
   * after the strip. Absent when the v1 entry was not rewritten.
   */
  strippedFrom?: string;
  migratedAt?: number;
}

export const V2_DEFAULT_PROVENANCE: V2Provenance = {
  location: 'anon',
  favoriteCategories: 'anon',
};

export const V2_LOCAL_ONLY_PROVENANCE: V2Provenance = {
  location: 'local-only',
  favoriteCategories: 'local-only',
};

function safeGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* ignore quota errors */
  }
}

function isV2Patch(v: unknown): v is V2PrefsPatch {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

function normalizeProvenance(v: unknown): V2Provenance {
  const o = (v && typeof v === 'object' ? v : {}) as Record<string, unknown>;
  const field = (x: unknown): V2ProvenanceField => (x === 'anon' ? 'anon' : 'local-only');
  return { location: field(o.location), favoriteCategories: field(o.favoriteCategories) };
}

function isUserPreferencesV2(v: unknown): v is UserPreferencesV2 {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  const location = o.location as Record<string, unknown> | undefined;
  if (!location || typeof location !== 'object') return false;
  if (location.type === 'comune' && typeof location.municipalityId !== 'string') return false;
  if (location.type !== 'comune' && location.type !== 'none') return false;
  return (
    typeof o.radiusKm === 'number' &&
    Number.isFinite(o.radiusKm) &&
    Array.isArray(o.favoriteCategories) &&
    o.favoriteCategories.every((c) => typeof c === 'string')
  );
}

/** Read the v2 record for an owner. Returns null when absent or malformed. */
export function loadV2Stored(owner: string | null): V2StoredRecord | null {
  const raw = safeGet(v2StoreKey(owner));
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const o = parsed as Record<string, unknown>;
    if (o.v !== 1 || !isUserPreferencesV2(o.prefs)) return null;
    return {
      v: 1,
      prefs: o.prefs,
      provenance: normalizeProvenance(o.provenance),
      pending: isV2Patch(o.pending) ? o.pending : null,
      locationNeedsConfirmation: o.locationNeedsConfirmation === true,
      convertedFrom: typeof o.convertedFrom === 'string' ? o.convertedFrom : undefined,
      strippedFrom: typeof o.strippedFrom === 'string' ? o.strippedFrom : undefined,
      migratedAt: typeof o.migratedAt === 'number' ? o.migratedAt : undefined,
    };
  } catch {
    return null;
  }
}

/** Overwrite the v2 record for an owner (full record). */
export function saveV2Stored(owner: string | null, record: V2StoredRecord): void {
  safeSet(v2StoreKey(owner), JSON.stringify(record));
}

/** Synchronous read-modify-write on the v2 record of an owner. */
export function updateV2Stored(
  owner: string | null,
  updater: (prev: V2StoredRecord) => V2StoredRecord
): void {
  const prev =
    loadV2Stored(owner) ??
    ({
      v: 1,
      prefs: { location: { type: 'none' }, radiusKm: DEFAULT_RADIUS_KM, favoriteCategories: [] },
      provenance: V2_DEFAULT_PROVENANCE,
      pending: null,
    } satisfies V2StoredRecord);
  saveV2Stored(owner, updater(prev));
}

/** Remove the v2 record of an owner (explicit full reset). */
export function clearV2Stored(owner: string | null): void {
  try {
    window.localStorage.removeItem(v2StoreKey(owner));
  } catch {
    /* ignore */
  }
}

/**
 * Claim the current anonymous v2 batch for the logged-in account: every field
 * becomes 'local-only' so a second login can never import it (v1 mirror:
 * `claimAnonStoredPrefs`).
 */
export function claimV2AnonStored(): void {
  const current = loadV2Stored(null);
  if (!current) return;
  saveV2Stored(null, { ...current, provenance: V2_LOCAL_ONLY_PROVENANCE });
}

/**
 * Non-reversible fingerprint of the v1 location (old-tab drift detector).
 *
 * A hash, NOT the JSON: the record must never re-embed the raw coordinates it
 * just finished stripping ("remove cached precise coords", plan l.138). FNV-1a
 * is enough — a collision only suppresses a recoverable conflict NOTICE, it
 * can never change a stored location.
 */
export function fingerprintV1Location(location: LocationChoice): string {
  const json = JSON.stringify(location);
  let hash = 0x811c9dc5;
  for (let i = 0; i < json.length; i++) {
    hash ^= json.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return `fnv1a-${hash.toString(16)}`;
}

/**
 * H1 (security sweep): rewrite the raw `gps` entry of EVERY v1 key once the
 * conversion that consumed it is durable.
 *
 * The three v1 stores (`fieravicino_user_prefs`, `:anon`, `:user:<id>`) used to
 * keep the record `{"type":"gps","coords":[lat,lng],...}` indefinitely — only
 * logout/reset removed it (plan l.78 "raw coords/name-label/ts are removed",
 * l.138, DoD l.179). The rewrite happens at the raw-JSON level so every other
 * field (outbox, provenance) is preserved untouched; a key whose location is
 * not a `gps` record is left alone.
 */
export function rewriteV1GpsLocations(replacement: LocationChoice, owner: string | null): void {
  const keys = [LEGACY_PREFS_KEY, ANON_PREFS_KEY];
  if (owner !== null) keys.push(userPrefsStorageKey(owner));
  for (const key of keys) rewriteGpsEntry(key, replacement);
}

/** One key of `rewriteV1GpsLocations`: gps entry in place, everything else kept. */
function rewriteGpsEntry(key: string, replacement: LocationChoice): void {
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(key);
  } catch {
    return;
  }
  if (!raw) return;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return;
  }
  if (!parsed || typeof parsed !== 'object') return;
  const record = parsed as Record<string, unknown>;
  const prefs =
    record.prefs && typeof record.prefs === 'object'
      ? (record.prefs as Record<string, unknown>)
      : null;
  // v2-shaped stores nest the location under `prefs`; the owner-less legacy
  // store holds it at the top level (`migrateLegacyPrefs`).
  const holder = prefs ?? record;
  const loc = holder.location;
  if (!loc || typeof loc !== 'object' || (loc as Record<string, unknown>).type !== 'gps') return;
  holder.location = replacement;
  try {
    window.localStorage.setItem(key, JSON.stringify(parsed));
  } catch {
    /* ignore quota errors */
  }
}

// ---------------------------------------------------------------------------
// One-time v1 → v2 conversion (runs only when the discovery gate enters v2)
// ---------------------------------------------------------------------------

export interface V2Conversion {
  prefs: UserPreferencesV2;
  provenance: V2Provenance;
  /** FNV-1a fingerprint of the v1 location — persisted so later v1 drift is detectable. */
  convertedFrom: string;
  /**
   * H1: what the raw v1 `gps` entry must be REPLACED with once this conversion
   * is durable — the resolved comune's public registry center (a v1 `cities`
   * entry), or `none` when nothing was resolved. Absent when the v1 entry
   * carries no precise coordinates (city/none conversions) and stays as it is.
   */
  v1Replacement?: LocationChoice;
  /**
   * H1: FNV-1a fingerprint of `v1Replacement` — persisted as `strippedFrom` so
   * the old-tab drift detector keeps working once the original entry is gone.
   */
  strippedFrom?: string;
  /** Completed, non-blocking outcome: identity is NULL, the user is asked to confirm. */
  needsConfirmation: boolean;
  notice?: PrefsSyncNotice;
  /**
   * false = a transient fault (registry/containment unreachable). The raw v1
   * coordinates are never persisted, but the outcome is NOT terminal: the
   * caller keeps the conversion in memory only, leaves the v1 store intact and
   * retries on the next session instead of writing a wrong `none`. A
   * `location-unavailable` notice is attached so the fallback to a national
   * feed is never SILENT (W5, plan l.61).
   */
  durable: boolean;
}

const NEEDS_CONFIRMATION_MESSAGE =
  'Non riusciamo a riconoscere il tuo comune: selezionalo per confermare la località.';
const NEEDS_CONFIRMATION_NOTICE: PrefsSyncNotice = {
  kind: 'needs-confirmation',
  message: NEEDS_CONFIRMATION_MESSAGE,
};
export const MULTI_CITY_MESSAGE =
  'Le località multiple non sono più supportate: abbiamo mantenuto il primo comune selezionato.';
const CONVERSION_FAILED_MESSAGE =
  'Non è stato possibile recuperare la tua località salvata: scegli il tuo comune, oppure ricarica la pagina.';
/** W5: a transient conversion fault must never settle SILENTLY to a national feed. */
const CONVERSION_FAILED_NOTICE: PrefsSyncNotice = {
  kind: 'location-unavailable',
  message: CONVERSION_FAILED_MESSAGE,
};

/**
 * Convert one v1 `LocationChoice` + favorites into `UserPreferencesV2`.
 *
 * Rules (plan l.78 / T011 acceptance):
 * - every v1 `location.radiusKm` is extracted into the independent
 *   `preferences.radiusKm` BEFORE the legacy location fields are dropped;
 * - a city resolves through the REGISTRY (exact name+province, never fuzzy):
 *   the first explicit choice of a multi-city list wins, with a notice;
 * - a cached GPS fix resolves ONCE through the containment endpoint with
 *   `accuracyMeters: 0` (saved snapshot, no current-fix claim) and the raw
 *   coordinates/name-label/ts are then dropped from the v1 stores too — never
 *   a nearest-name import (H1: `v1Replacement`/`strippedFrom`);
 * - an unresolved first choice (homonym without province, renamed/merged
 *   comune, ambiguous containment) ends as `needs_confirmation`, never as a
 *   silently chosen different city.
 */
export async function convertV1PrefsToV2(input: {
  location: LocationChoice;
  favoriteCategories: string[];
  provenance: V2Provenance;
}): Promise<V2Conversion> {
  const { location, provenance } = input;
  const convertedFrom = fingerprintV1Location(location);
  const radiusKm =
    (location.type === 'cities' || location.type === 'gps') && Number.isFinite(location.radiusKm)
      ? location.radiusKm
      : DEFAULT_RADIUS_KM;
  const base: UserPreferencesV2 = {
    location: { type: 'none' },
    radiusKm,
    favoriteCategories: [...input.favoriteCategories],
  };
  const done = (prefs: UserPreferencesV2, extra: Partial<V2Conversion> = {}): V2Conversion => ({
    prefs,
    provenance,
    convertedFrom,
    needsConfirmation: false,
    durable: true,
    ...extra,
  });

  if (location.type === 'none') return done(base);

  if (location.type === 'gps') {
    const [lat, lng] = location.coords;
    let result: ResolveLocationResult;
    try {
      result = await resolvePoint(lat, lng, 0);
    } catch {
      // Transient fault: not persisted, retried next session — and NEVER
      // silent (W5): the user is told why the feed fell back to national.
      return done(base, { durable: false, notice: CONVERSION_FAILED_NOTICE });
    }
    if (result.kind === 'resolved' && result.municipality.path) {
      // H1: the raw fix must not outlive this conversion — the v1 entry is
      // rewritten to the resolved comune's PUBLIC registry center (plan
      // l.78/138, DoD l.179: no precise GPS in persistent stores).
      const m = result.municipality;
      const v1Replacement: LocationChoice = {
        type: 'cities',
        cities: [{ name: m.name, lat: m.center.lat, lng: m.center.lng, province: m.province }],
        radiusKm,
      };
      return done(
        { ...base, location: { type: 'comune', municipalityId: m.id } },
        { v1Replacement, strippedFrom: fingerprintV1Location(v1Replacement) }
      );
    }
    // Ambiguous/unavailable: still a durable outcome (needs confirmation), so
    // the raw coordinates leave the v1 store all the same — they are never
    // replayed as active geography.
    const v1Replacement: LocationChoice = { type: 'none' };
    return done(base, {
      needsConfirmation: true,
      notice: NEEDS_CONFIRMATION_NOTICE,
      v1Replacement,
      strippedFrom: fingerprintV1Location(v1Replacement),
    });
  }

  const first = location.cities[0];
  if (!first) return done(base);

  let hit: RegistryCityItem | null;
  try {
    hit = await resolveRegistryPair(first.name, first.province);
  } catch {
    // Transient fault: not persisted, retried next session — and NEVER silent
    // (W5): the user is told why the feed fell back to national.
    return done(base, { durable: false, notice: CONVERSION_FAILED_NOTICE });
  }

  if (hit) {
    const migratedFromList = location.cities.length > 1;
    return done(
      { ...base, location: { type: 'comune', municipalityId: hit.id } },
      migratedFromList ? { notice: { kind: 'migration', message: MULTI_CITY_MESSAGE } } : {}
    );
  }

  // No unique match (0 rows, a province-less homonym, or a pathless row):
  // an unresolved first choice asks — it is never resolved by guessing.
  return done(base, { needsConfirmation: true, notice: NEEDS_CONFIRMATION_NOTICE });
}
